import { useDeferredValue, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { SUPPLIER_ROLES, SUPPLIER_ROLE_LABELS, type SupplierRole } from "@torre/assets";

import { Badge, Button, EmptyState, Input, Select, Table, type TableColumn } from "../../../components/ui";
import { SupplierFormModal } from "../components/SupplierFormModal";
import { ErrorState, Loader, SectionFrame } from "../components/shared";
import { useSuppliers } from "../hooks/useActivos";
import { langOf, optionsOf, supplierRoleLabel } from "../lib/vocab";
import type { SupplierRow } from "../services/activosApi";

// ============================================================================
// Proveedores de activos: quién vende y quién repara. RFC único por compañía.
// Archivo común, idéntico en CC-CONSOLE y WAYAK.
// ============================================================================

const PARAM = "proveedor";

export default function ProveedoresSection() {
  const { t, i18n } = useTranslation("activos");
  const lang = langOf(i18n.language);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [role, setRole] = useState<SupplierRole | "">("");
  const [creating, setCreating] = useState(false);
  const search = useDeferredValue(q.trim());
  const list = useSuppliers({ q: search || undefined, role });
  const all = useSuppliers({});
  const rows = list.data?.items || [];
  const openId = params.get(PARAM);
  const editing = openId ? all.data?.items.find((s) => s.id === openId) || null : null;

  const open = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set(PARAM, id);
    else next.delete(PARAM);
    setParams(next, { replace: true });
  };

  const columns: TableColumn<SupplierRow>[] = [
    { key: "name", header: t("fields.name"), render: (s) => <span className="font-medium text-content">{s.name}</span> },
    { key: "rfc", header: t("fields.rfc"), render: (s) => s.rfc || "—" },
    {
      key: "roles",
      header: t("fields.roles"),
      render: (s) => (
        <div className="flex flex-wrap gap-1">
          {(s.roles || []).map((r) => (
            <Badge key={r} tone="info">
              {supplierRoleLabel(r, lang)}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: "contact",
      header: t("fields.contact"),
      hideOnMobile: true,
      render: (s) => [s.contact?.name, s.contact?.email, s.contact?.phone].filter(Boolean).join(" · ") || "—",
    },
  ];

  return (
    <SectionFrame
      title={t("proveedores.title")}
      subtitle={t("proveedores.subtitle")}
      actions={
        <Button size="sm" icon={<i className="ri-add-line" />} onClick={() => setCreating(true)} data-testid="supplier-create">
          {t("proveedores.create")}
        </Button>
      }
      filters={
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input aria-label={t("filters.searchSupplier")} placeholder={t("filters.searchSupplier")} value={q} onChange={(e) => setQ(e.target.value)} icon={<i className="ri-search-line" />} />
          <Select
            aria-label={t("fields.roles")}
            value={role}
            onChange={(e) => setRole(e.target.value as SupplierRole | "")}
            options={[{ value: "", label: t("filters.allRoles") }, ...optionsOf(SUPPLIER_ROLES, SUPPLIER_ROLE_LABELS, lang)]}
          />
        </div>
      }
    >
      {list.isLoading ? (
        <Loader />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : rows.length === 0 ? (
        search || role ? (
          <EmptyState icon="ri-filter-off-line" title={t("proveedores.noResults.title")} description={t("proveedores.noResults.text")} />
        ) : (
          <EmptyState
            icon="ri-store-2-line"
            title={t("proveedores.empty.title")}
            description={t("proveedores.empty.text")}
            action={
              <Button size="sm" icon={<i className="ri-add-line" />} onClick={() => setCreating(true)}>
                {t("proveedores.create")}
              </Button>
            }
          />
        )
      ) : (
        <Table columns={columns} rows={rows} rowKey={(s) => s.id} onRowClick={(s) => open(s.id)} />
      )}
      {list.data?.truncated ? <p className="text-xs text-content-muted">{t("proveedores.truncated")}</p> : null}

      {creating ? <SupplierFormModal onClose={() => setCreating(false)} onOpenSupplier={(id) => open(id)} /> : null}
      {editing ? <SupplierFormModal key={editing.id} supplier={editing} onClose={() => open(null)} onOpenSupplier={(id) => open(id)} /> : null}
    </SectionFrame>
  );
}
